import { Alert, Checkbox, Item, ItemHeader, ItemTitle, ScrollArea, Skeleton } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import type { KnowledgeNodeInSet } from '@/entities/knowledge-set';
import { SmartSearchInput, useSmartSearchPaginated } from '@/features/smart-search';

type Props = {
  knowledgeSetId?: number;
  isCreating: boolean;
  localNodeIds: Set<number>;
  onToggleNode: (nodeId: number, isInSet: boolean) => void;
  onNodesLoaded?: (nodes: KnowledgeNodeInSet[]) => void;
  readonly?: boolean;
};

export const AllNodesTab: FC<Props> = ({
  knowledgeSetId,
  isCreating,
  localNodeIds,
  onToggleNode,
  onNodesLoaded,
  readonly,
}) => {
  const { t } = useTranslation(['knowledgeSets', 'common']);

  const baseFilters = useMemo(
    () => ({
      ...(knowledgeSetId && !isCreating ? { knowledgeSetContext: knowledgeSetId } : {}),
      isDeleted: false,
    }),
    [knowledgeSetId, isCreating],
  );

  const {
    searchState,
    items,
    page,
    setPage,
    totalPages,
    predicateInfo,
    handleSearch,
    handleClear,
    isLoading,
    isSearchAnimating,
    searchError,
  } = useSmartSearchPaginated<'knowledge', KnowledgeNodeInSet>({
    entityType: 'knowledge',
    baseFilters,
    pageSize: 50,
  });

  useEffect(() => {
    if (items.length > 0) onNodesLoaded?.(items);
  }, [items, onNodesLoaded]);

  const handleToggle = useCallback(
    (nodeId: number, currentlyInSet: boolean) => {
      onToggleNode(nodeId, !currentlyInSet);
    },
    [onToggleNode],
  );

  if (isLoading && items.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden">
      <SmartSearchInput
        value={searchState.value}
        onChange={searchState.onChange}
        mode={searchState.searchMode}
        onModeChange={searchState.setSearchMode}
        onSearch={handleSearch}
        onClear={handleClear}
        predicateInfo={predicateInfo}
        error={searchError}
        isLoading={isSearchAnimating}
        placeholder={t('knowledgeSets:searchNodesPlaceholder')}
        disabled={readonly}
      />

      {items.length === 0 && !isLoading ? (
        <Alert className="text-muted-foreground p-4" title={t('knowledgeSets:noNodes')} />
      ) : (
        <ScrollArea className="flex-1">
          <ul className="flex flex-col gap-2">
            {items.map((node) => {
              const isInSet = localNodeIds.has(node.id);
              return (
                <li key={node.id}>
                  <Item
                    variant="outline"
                    size="sm"
                    className={readonly ? '!overflow-hidden' : 'cursor-pointer !overflow-hidden'}
                    onClick={readonly ? undefined : () => handleToggle(node.id, isInSet)}
                  >
                    <ItemHeader className="flex items-center !justify-start gap-3 min-w-0 overflow-hidden">
                      <Checkbox
                        className="!w-auto shrink-0"
                        value={isInSet}
                        onChange={readonly ? undefined : () => handleToggle(node.id, isInSet)}
                        disabled={readonly}
                      />
                      <div className="flex flex-col gap-1 min-w-0">
                        <ItemTitle className="text-sm font-medium !w-auto break-words">{node.name}</ItemTitle>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground shrink-0">{node.type}</span>
                          <NavLink
                            to={`/knowledge/${node.id}`}
                            className="text-xs text-primary hover:underline shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t('knowledgeSets:goToNode')}
                          </NavLink>
                        </div>
                      </div>
                    </ItemHeader>
                  </Item>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-2">
          <IconButton
            variant="outline"
            size="icon"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            label="Previous page"
            icon={<ChevronLeftIcon className="size-4" />}
          />
          <span className="text-sm text-muted-foreground">{`${page + 1} / ${totalPages}`}</span>
          <IconButton
            variant="outline"
            size="icon"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            label="Next page"
            icon={<ChevronRightIcon className="size-4" />}
          />
        </div>
      )}
    </div>
  );
};
